<template>
  <b-row>
    <b-col
      :md="
        hasAccessAll('delivery-load-adjustment:read', 'delivery-load-adjustment:update') ? 6 : 12
      "
    >
      <b-skeleton-wrapper v-if="isLoading" :loading="isLoading">
        <template #loading>
          <b-card>
            <b-skeleton width="100%" />
          </b-card>
        </template>
      </b-skeleton-wrapper>
      <b-card v-else class="mc-proof-tab">
        <div class="mc-liveview--fo-sidebar-history">
          <div
            v-for="(item, index) in activity.filter((item) => item.status !== 'UNCOMPLETED')"
            :key="index"
            class="mc-liveview--fo-sidebar-history-item clearfix"
          >
            <div class="mc-liveview--fo-sidebar-history-item-line">
              <i class="mc-fa icon-outline_arrow-circle-right"></i>
            </div>
            <div class="mc-liveview--fo-sidebar-history-item-detail">
              <p style="grid-area: history-date">
                {{ getCompletedDateLabel(item.completed_on) }}
              </p>
              <span class="text-muted" style="grid-area: history-time">{{
                formatIndexDate(item.completed_on).time
              }}</span>
              <div style="grid-area: history-detail">
                <p class="mb-2 d-flex align-items-center gap-2">
                  {{ getActivityActorLabel(item.type) }}
                  <router-link
                    v-if="!item.vendor_name"
                    class="text-secondary-route-link"
                    :to="{ name: 'Fleet Task Detail', params: { id: item.fleet_task.id } }"
                  >
                    {{ item?.fleet_task?.fo_number || '-' }}
                  </router-link>
                  <a v-else class="mc-paragraph text-cursor">
                    {{ item?.vendor_name ?? '' }}
                  </a>
                  <b-alert :variant="item.is_transit ? 'warning' : 'success'" show class="m-0 p-1">
                    {{ getLocationBadgeLabel(item) }}
                  </b-alert>
                </p>
                <p class="__title mb-2">
                  {{ getContactPersonLabel(item.type) }}
                  <strong>{{ item.contact_person_name }}</strong>
                </p>
                <p class="__title mb-2">
                  {{ getAddressLabel(item.type) }}
                  <strong>{{ item.address.name }}</strong>
                </p>
                <p class="__title mb-2">
                  {{ noteLabel }}
                  <strong>{{ item.note || '-' }}</strong>
                </p>
                <div class="text-muted">
                  <p class="__title mb-2">
                    {{ additionalNoteLabel }}
                  </p>
                  <McChipList
                    v-if="notes && notes.length"
                    :items="notes"
                    :max-visible="maxVisibleNotes"
                    v-bind="{
                      itemTextField: 'note',
                      itemAttachmentUrlField: 'document_attachment_url',
                    }"
                  />
                  <span v-else>-</span>
                </div>
                <div
                  v-if="item.attachments?.length"
                  class="mc-proof-tab__wrapper __attachments mb-2"
                >
                  <p class="__title">
                    {{ photoLabel }}
                  </p>
                  <div class="__attachments-wrapper">
                    <div
                      v-for="(attachment, idx) in item.attachments"
                      :key="idx"
                      class="__attachment"
                    >
                      <div class="__attachment-img" @click="handleOpenPreview(idx, index)">
                        <img :src="attachment.url" :alt="getAttachmentFileName(idx)" />
                      </div>

                      <p>{{ getAttachmentFileName(idx) }}</p>
                      <button
                        class="__download-btn"
                        @click="handleDownloadImg(attachment.url, getAttachmentFileName(idx))"
                      >
                        <i class="icon-outline_download"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="mc-liveview--fo-sidebar-history-item clearfix">
            <div class="mc-liveview--fo-sidebar-history-item-line">
              <i class="mc-fa icon-fill_radio-button-on"></i>
            </div>
          </div>
        </div>

        <PreviewImage
          v-if="activeAttachmentIdx !== null"
          :attachments="
            activity.filter((item) => item.status !== 'UNCOMPLETED')[selectedActivityIndex]
              ?.attachments
          "
          :activeAttachmentIdx="activeAttachmentIdx"
          @update:activeAttachmentIdx="activeAttachmentIdx = $event"
          @handleDownloadImg="handleDownloadImg"
        />
      </b-card>
    </b-col>
    <b-col
      v-if="hasAccessAll('delivery-load-adjustment:read', 'delivery-load-adjustment:update')"
      md="6"
    >
      <b-card class="mc-proof-tab">
        <McTable
          v-if="activityLoaded"
          class="mc-proof-table"
          :withCard="false"
          searchable
          stickyHeader
          :hasCheckbox="false"
          :hasSelectAll="false"
          :fields="fields"
          :apiClient="apiClient"
        >
          <template #customDisplayItems>
            <div class="flex custom-display-items">
              <span>
                {{ quantityChangeHistoryLabel }}
              </span>
              <McBadge
                :text="activityBadgeLabel"
                :variant="isPickUp ? 'primary' : 'success'"
                size="medium"
                type="button"
              />
            </div>
          </template>
          <template #[`cell(created_on)`]="{ value }">
            {{ formatDateWithTz(value) }}
          </template>
          <template #[`cell(product)`]="{ value }">
            {{ value.name }}
            <div class="text-muted text-sm">
              {{ value.type }}
            </div>
          </template>
        </McTable>
      </b-card>
    </b-col>
    <!-- Modals -->
    <b-modal
      :id="`mc-modal-customer-notes`"
      ref="mc-modal-customer-notes"
      :title="customerNotesModalTitle"
      class="mc-modal"
      size="md"
      centered
    >
      <div class="card-body p-2">
        <div class="table-container-outer">
          <b-table
            class="mb-0 custom-modal-table"
            :fields="customerNotesField"
            :items="customerNotesTableData"
          >
            <template #head()>
              <span class="header-text">{{ customerNotesModalTitle }}</span>
            </template>
            <template #cell()="data">
              <span class="item-text">{{ data.value }}</span>
            </template>
          </b-table>
        </div>
      </div>

      <template #modal-footer="{ cancel }">
        <div class="d-flex mx-0">
          <McButton
            variant="primary"
            class="me-2"
            :text="$t('global.action.close')"
            @click="cancel()"
          />
        </div>
      </template>

      <PreviewImage
        v-if="activeAttachmentIdx !== null"
        :attachments="activityDetail?.attachments"
        :activeAttachmentIdx="activeAttachmentIdx"
        @update:activeAttachmentIdx="activeAttachmentIdx = $event"
        @handleDownloadImg="handleDownloadImg"
      />
    </b-modal>
  </b-row>
</template>

<script>
import McTable from '@/components/McTable'
import McBadge from '@/components/McBadge'
import McButton from '@/components/McButton'
import { mapState, mapGetters } from 'vuex'
import PreviewImage from './PreviewImage.vue'

import McChipList from '@/components/McChipList'
import * as DeliveryActivity from '@/services/rest-api/lib/delivery/activity'
import * as historyApi from '@/services/rest-api/lib/delivery/history'
import filterMappings from '../lib/constants/filter-mappings'

import { formatDateWithTz, formatIndexDate, isToday } from '@/helpers/tzDate'

export default {
  components: {
    PreviewImage,
    McTable,
    McBadge,
    McButton,
    McChipList,
  },
  props: {
    activeTab: {
      type: String,
      required: true,
    },
  },
  emits: ['update:isLoading'],
  data() {
    return {
      activity: {},
      activeAttachmentIdx: null,
      selectedActivityIndex: 0,
      isLoading: false,
      activityLoaded: false,
      maxVisibleNotes: 3,
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    ...mapState('delivery', ['deliveryStatus', 'activities', 'shipmentType']),
    isPickUp() {
      if (!Array.isArray(this.activity)) return false
      return this.activity.some((item) => item.type === 'PICKUP')
    },
    getEventFilterValue() {
      if (this.isPickUp) {
        return 'FleetPlanning.DeliveryLoadUpdatedPicked'
      } else {
        return 'FleetPlanning.DeliveryLoadUpdatedDropped'
      }
    },
    noteLabel() {
      return `${this.$t('delivery.proofTab.fields.note')}:`
    },
    additionalNoteLabel() {
      return `${this.$t('delivery.proofTab.fields.additionalNote')}:`
    },
    customerNotesModalTitle() {
      return this.$t('delivery.proofTab.fields.additionalNote')
    },
    photoLabel() {
      return this.$t('global.photo')
    },
    quantityChangeHistoryLabel() {
      return this.$t('delivery.loadsTable.action.quantityChangeHistory')
    },
    activityBadgeLabel() {
      return this.$t(
        this.isPickUp
          ? 'delivery.loadsTable.history.filter.pickup'
          : 'delivery.loadsTable.history.filter.delivery'
      )
    },
    fields() {
      return [
        {
          label: this.$t('delivery.loadsTable.history.fields.changeTime'),
          key: 'created_on',
          isSortable: false,
        },
        {
          label: this.$t('delivery.loadsTable.history.fields.updatedBy'),
          key: 'created_by',
          isSortable: false,
        },
        {
          label: this.$t('delivery.loadsTable.history.fields.productName'),
          key: 'product',
          isSortable: false,
        },
        {
          label: this.$t('delivery.loadsTable.history.fields.previousQuantity'),
          key: 'old_quantity',
          isSortable: false,
        },
        {
          label: this.$t('delivery.loadsTable.history.fields.newQuantity'),
          key: 'new_quantity',
          isSortable: false,
        },
        {
          label: this.$t('delivery.loadsTable.history.fields.changeReason'),
          key: 'reason',
          isSortable: false,
          columnHasTooltip: true,
          columnTooltipLength: 20,
        },
      ]
    },
    apiClient() {
      const baseFilters = [
        {
          key: 'event',
          values: [this.getEventFilterValue],
        },
      ]

      const queryParamFilters = baseFilters.map(
        (filter) => filterMappings[filter.key]?.(filter.key, filter.values) ?? undefined
      )

      return {
        get: async (queryParam) => {
          const logResult = await historyApi.getLog(
            this.$route.params.id,
            queryParam,
            queryParamFilters
          )

          const keyMap = {
            product_name: 'product_name',
            product_type_name: 'product_type_name',
            uom: 'uom',
            old_qty: 'old_quantity',
            new_qty: 'new_quantity',
            reason: 'reason',
          }

          const remodeledLogData = logResult.data.paginated_result.map((log) => {
            const extractedData = {
              product_name: '',
              product_type_name: '',
              product: {
                name: '',
                type: '',
              },
              uom: '',
              old_quantity: '',
              new_quantity: '',
              reason: '',
              created_on: log.created_on,
              created_by: log.created_by,
            }

            log.details.forEach((item) => {
              if (keyMap[item.key]) {
                extractedData[keyMap[item.key]] = item.value
              }
            })
            extractedData.product = {
              name: extractedData.product_name,
              type: extractedData.product_type_name,
            }
            extractedData.old_quantity = `${extractedData.old_quantity} ${extractedData.uom}`
            extractedData.new_quantity = `${extractedData.new_quantity} ${extractedData.uom}`

            return extractedData
          })

          logResult.data.paginated_result = remodeledLogData

          return logResult
        },
      }
    },
    notes() {
      if (!this.activity.length) return []
      const raw = this.activity[this.selectedActivityIndex]?.customer_notes
      if (Array.isArray(raw)) return raw
      if (typeof raw === 'string') {
        return raw.length
          ? raw
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : []
      }
      return []
    },
    customerNotesField() {
      return [
        {
          key: 'columnCustomerNote',
          label: this.$t('delivery.proofTab.fields.additionalNote'),
          thClass: 'mc-table--50',
          sortable: false,
        },
      ]
    },
    customerNotesTableData() {
      return this.notes.map((item, index) => {
        return {
          id: index,
          columnCustomerNote: item,
          type: 'data',
        }
      })
    },
  },
  async created() {
    this.isLoading = true
    this.$emit('update:isLoading', true)
    this.activityLoaded = false

    if (
      ['Diambil', 'Sedang Transit', 'Terkirim'].includes(this.deliveryStatus) ||
      (!['Diambil', 'Sedang Transit', 'Terkirim'].includes(this.deliveryStatus) &&
        this.$store.state.delivery.shipmentType === 'TRANSIT')
    ) {
      const res = await DeliveryActivity.get(this.$route.params.id)
      if (!res.data) return

      this.$store.dispatch('delivery/setActivities', res.data)
    }

    if (['PICKUP', 'DROP'].includes(this.activeTab)) {
      this.activity = this.activities.filter((activity) => activity.type === this.activeTab)
    }

    this.activityLoaded = true
    this.$emit('update:isLoading', false)
    this.isLoading = false
  },
  methods: {
    formatDateWithTz,
    formatIndexDate,
    isToday,
    getCompletedDateLabel(completedOn) {
      return this.isToday(completedOn)
        ? this.$t('global.time.today')
        : this.formatIndexDate(completedOn).date
    },
    getActivityActorLabel(type) {
      return this.$t(
        type === 'PICKUP'
          ? 'delivery.proofTab.history.pickedBy'
          : 'delivery.proofTab.history.deliveredBy'
      )
    },
    getLocationBadgeLabel(item) {
      if (item.is_transit) {
        return this.$t('delivery.fields.transitLocation')
      }

      return this.$t(
        item.type === 'PICKUP' ? 'delivery.originLocation' : 'delivery.destinationLocation'
      )
    },
    getContactPersonLabel(type) {
      return `${this.$t(
        type === 'PICKUP'
          ? 'delivery.proofTab.fields.senderName'
          : 'delivery.proofTab.fields.receiverName'
      )}:`
    },
    getAddressLabel(type) {
      return `${this.$t(
        type === 'PICKUP'
          ? 'delivery.proofTab.fields.pickupLocation'
          : 'delivery.proofTab.fields.deliveryLocation'
      )}:`
    },
    getAttachmentFileName(idx) {
      return `${this.photoLabel.toLowerCase()}${idx + 1}.jpg`
    },
    async handleDownloadImg(imgUrl, fileName) {
      const res = await fetch(imgUrl, {
        headers: new Headers({
          'Cache-Control': 'no-cache',
        }),
      })
      if (!res) return

      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      Object.assign(document.createElement('a'), { href: url, download: fileName }).click()
      URL.revokeObjectURL(url)
    },
    handleOpenPreview(attachmentIdx, activityIndex) {
      this.activeAttachmentIdx = attachmentIdx

      const filteredActivities = this.activity.filter((item) => item.status !== 'UNCOMPLETED')
      this.selectedActivityIndex =
        activityIndex >= 0 && activityIndex < filteredActivities.length ? activityIndex : 0
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-proof-tab {
  &__card {
    font-size: 13px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
    color: $teal-400;
  }

  p {
    margin: 0;
  }

  .__title {
    color: $mep-color-text-300;
  }

  .__value-bold {
    font-weight: 600;
  }

  .__attachments {
    gap: 12px;
  }

  .__attachments-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  .__attachment {
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;

    p {
      color: $teal-400;
    }

    .__attachment-img {
      border-radius: 4px;
      height: 100px;
      aspect-ratio: 1/1;
      overflow: hidden;
      cursor: pointer;

      img {
        height: 100%;
        width: 100%;
        object-fit: cover;
      }
    }

    .__download-btn {
      position: absolute;
      top: 3px;
      right: 3px;
      height: 24px;
      width: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid $blue;
      border-radius: 3px;
      background-color: $white;

      i::before {
        font-size: 16px;
        color: $blue;
      }
    }
  }

  .mc-liveview--fo-sidebar-history-item::before {
    height: 95%;
  }
}

.info-value-customer-notes {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.notes-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-start;
}

.note-chip {
  display: inline-flex;
  align-items: center;
  background: $blue;
  color: $white;
  border-radius: 18px;
  font-size: 12px;
  overflow: hidden;
  height: 25px;
}

/* text area */
.note-text {
  padding: 0 14px;
  display: flex;
  align-items: center;
}

/* icon area kanan */
.note-attachment {
  width: 34px;
  height: 100%;
  border: none;
  border-left: 2px solid $white;
  background: $blue;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}

.note-attachment i::before {
  font-size: 16px;
  color: $white;
}

.note-chip.more-count {
  background: $green;
  min-width: 25px;
  height: 25px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  padding: 0 8px;
  font-weight: 600;
}

.notes-actions {
  margin-top: 6px;
  width: 100%;
}

.toggle-notes {
  background: transparent;
  border: none;
  color: $cyan;
  cursor: pointer;
  padding: 0;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.notes-actions .toggle-notes {
  display: inline-block;
  margin-left: 0;
}

.chev::before {
  position: absolute;
  font-size: 16px;
  padding-top: 2px !important;
  font-weight: 500;
}

.table-container-outer {
  border: 1px solid $gray;
  border-radius: 4px;
  max-height: 300px;
  overflow-y: auto;
}

/* Table Row Styling */
::v-deep .custom-modal-table td,
::v-deep .custom-modal-table th {
  padding: 12px 16px !important;
  border-bottom: 1px solid $gray !important;
}

/* Text Styling differentiation */
.header-text {
  font-weight: 600;
}
</style>
<style lang="scss">
@media only screen and (max-width: 1445px) {
  .mc-proof-table {
    .before-table {
      .custom-display-items {
        display: block;
        width: 100%;
        padding-bottom: 16px;
      }

      .slot-item-end {
        justify-content: start !important;
      }
    }
  }
}
</style>
